// Polyfills
import './modules/polyfills/polyfills';
// Types
import './types/globals';
import { KeyValuePairs } from './types/common';
import { Data } from './types/hit';
import { DOT } from './classes/dot.class';
// Methods
import { syncSid } from './modules/sid/sync';
import { addSpenttime } from './modules/spenttime/spenttime';
import { addLinkdecoration } from './modules/linkdecoration/linkdecoration';
import { runMarker } from './modules/marker/marker';
import { configure, _fireAfterCfgEvent } from './modules/config/config';
import { handleImpress } from './modules/impress/impress';
import { finishLazyHitting, initLazyHitting } from './modules/lazyHitting/lazyHitting';
import { mouseDown } from './modules/events/mousedown/mousedown';
import { addLoad, initLoad } from './modules/load/load';
import { addError, initError } from './modules/error/error';
import { getGeometry } from './modules/geometry/geometry';
import { getDataDot, getDataDotData } from './modules/utils/html/traversing';
import { initIframeBridge } from './modules/utils/html/iframe';
// Constants
import { LAZY_HITTING_ENABLED, SERVICE_VARIANT } from './constants/index';

// IVA npm library
import '@iva/refresher';
import { startErrorHandling } from '@iva/error-reporter';
import { SID_UTILS } from '@iva/utils';

startErrorHandling({
  endpoint: 'dot-static',
  scriptName: 'dot-fulltext.js',
});

// Fulltext utils
const MOUSE_EVENT_KEYBOARD_KEYS = <const>['ctrlKey', 'altKey', 'shiftKey', 'metaKey'];
const DATA_DOT_DATA_ATTR = 'data-dot-data';

/**
 * Add extra dataDot attributes to provided element
 * returned cleanup function will recover element to its original state
 * @param {Element} element
 * @param {Record<string, any>} dataToInject
 * @returns {Function} removes injected data
 */
function injectTemporaryDataDotData(element: Element, dataToInject: Record<string, any>): () => void {
  let dotData = {};
  // eslint-disable-next-line init-declarations
  let originalDotDataValue: string | undefined;

  if (element.hasAttribute(DATA_DOT_DATA_ATTR)) {
    originalDotDataValue = element.getAttribute(DATA_DOT_DATA_ATTR);
    try {
      dotData = JSON.parse(originalDotDataValue);
    } catch {
      dotData = {};
    }
  }

  element.setAttribute(
    DATA_DOT_DATA_ATTR,
    JSON.stringify({
      ...dotData,
      ...dataToInject,
    })
  );

  // Return cleanup function
  return () => {
    if (originalDotDataValue) {
      element.setAttribute(DATA_DOT_DATA_ATTR, originalDotDataValue);
    } else {
      element.removeAttribute(DATA_DOT_DATA_ATTR);
    }
  };
}

SID_UTILS.removeInvalidSidCookie();

class DOT_FULLTEXT extends DOT {
  constructor() {
    super(SERVICE_VARIANT.FULLTEXT);
    runMarker(this);
    syncSid(this);

    /* Include all additional modules */
    addLoad(this);
    addError(this);
    addSpenttime(this);
    addLinkdecoration(this);
  }

  /**
   * If true, button code is added to mouse events
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
   */
  private _addMouseButtonCode = true;

  /**
   * if true, logs also pressed extra keys with mouse event
   */
  private _addKeyboardKeys = true;

  /**
   * @see getDataDot
   */
  public getDataDot = getDataDot;

  /**
   * Calls internal getDataDotData and assing result to data object
   * @param {HTMLElement} element
   * @param {Data} data
   * @returns {Data}
   */
  public getDataDotData(element: HTMLElement, data?: Data): Data {
    data = data || {};
    data.d = data.d || {};

    Object.assign(data.d, getDataDotData(element));
    return data;
  }

  /**
   * calls getGeometry and add result to data object
   * @param {Data} data
   */
  public addGeometry(data: Data): void {
    data.d = data.d || {};

    Object.assign(data.d, getGeometry(this._dataElms));
  }

  /**
   * Returns new DOT_FULLTEXT instance
   */
  public getNewInstance(): DOT_FULLTEXT {
    return new DOT_FULLTEXT();
  }

  /**
   * Fulltext custom mousedown handler
   * @param {MouseEvent} event
   * @returns {void}
   */
  public mouseDown(event: MouseEvent): void {
    const target = (event.target || event.srcElement) as Element | Node | EventTarget | undefined;

    // eslint-disable-next-line init-declarations
    let cleanInjected: (() => void) | undefined;

    let element = target;
    if (target && 'nodeName' in target && !('hasAttribute' in target)) {
      // try `parentElement` if target does not have 'hasAttribute' method
      element = target.parentElement;
    }

    // Check if it is htmlElement. Can be `null`
    if (element && 'hasAttribute' in element) {
      // Fulltext Ignore mousedown feature
      let currentTargetElement = element;
      while (currentTargetElement && currentTargetElement !== document.documentElement) {
        if ('hasAttribute' in currentTargetElement && currentTargetElement.hasAttribute('data-dot-ignore')) {
          // We should ignore this one
          return;
        }
        currentTargetElement = currentTargetElement.parentElement;
      }

      // eslint-disable-next-line init-declarations
      let dataToInject: Record<string, any> | undefined;

      // Mouse button code feature
      if (this._addMouseButtonCode) {
        dataToInject = dataToInject || {};
        dataToInject.mouseButtonCode = event.button;
      }

      if (this._addKeyboardKeys) {
        // Keyboard key feature
        const keyboardKeys: string[] = [];
        for (let keyIndex = 0; keyIndex < MOUSE_EVENT_KEYBOARD_KEYS.length; keyIndex++) {
          const keyName = MOUSE_EVENT_KEYBOARD_KEYS[keyIndex];
          if (event[keyName]) {
            // remove suffix `Key` and add value
            keyboardKeys.push(keyName.replace('Key', ''));
          }
        }

        if (keyboardKeys.length > 0) {
          dataToInject = dataToInject || {};
          dataToInject.keyboardKey = keyboardKeys;
        }
      }

      if (dataToInject) {
        // Add extra event data
        cleanInjected = injectTemporaryDataDotData(element, dataToInject);
      }
    }

    // Original function handler
    mouseDown(this, event, this._cfg.mousedownDataDotData);

    // Call clean after log
    if (typeof cleanInjected === 'function') {
      cleanInjected();
    }
  }

  /**
   * Configures DOT instance
   *
   * @param customConfig config data object
   * @param impressCallback called when impress is sent
   */
  public cfg(customConfig: KeyValuePairs<unknown>, impressCallback?: () => void): void {
    configure(this, customConfig);

    if (customConfig) {
      if ('addMouseButtonCode' in customConfig) {
        this._addMouseButtonCode = !!customConfig.addMouseButtonCode;
      }

      if ('addKeyboardKeys' in customConfig) {
        this._addKeyboardKeys = !!customConfig.addKeyboardKeys;
      }

      if (customConfig.lazyLoadingEnabled === true) {
        initLazyHitting(this);
      } else if (customConfig.lazyLoadingEnabled === false && this.lazyHittingStatus === LAZY_HITTING_ENABLED) {
        finishLazyHitting(this, customConfig);
        return;
      }
    }

    if (this._cfg.impress) {
      handleImpress(this, impressCallback);
    }

    if (this.configured) {
      return;
    }
    this.configured = true;

    _fireAfterCfgEvent(this);

    if (this._cfg.mousedown) {
      document.addEventListener('mousedown', (e: MouseEvent) => {
        this.mouseDown(e);
      });
    }

    if (this._cfg.load) {
      initLoad(this);
    }

    if (this._cfg.error) {
      initError(this);
    }

    initIframeBridge(this);
  }
}

window.DOT = new DOT_FULLTEXT();
